import React, { useState, useEffect } from 'react';
/**
 * GoogleAdsBanner.js - Displays Google AdSense ads at the bottom of the viewport
 * 
 * IMPORTANT: Before deploying to production:
 * 1. Replace "XXXXXXXXXX" with your actual ad slot ID in the data-ad-slot attribute
 * 2. Test the ad display in different screen sizes (desktop & mobile)
 * 3. Ensure the 60px height (50px for mobile) works with your layout
 *
 * Note: Google AdSense requires approval before ads will display.
 * You may see empty spaces until your account is approved.
 */
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Typography, Button, Box, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useNavigate } from 'react-router-dom';
import { isDebugFakePremium } from './web3/debugContractOverrides';

const useStyles = makeStyles((theme) => ({
  adBannerRoot: {
    position: 'fixed',
    bottom: 0, // Position at the very bottom of the viewport
    left: 0,
    width: '100%',
    zIndex: 1000, // Lower than bottomContainer
    height: '60px', // Fixed height of 60px to accommodate standard ad size
    maxHeight: '60px', // Max height constraint
    overflow: 'hidden', // Prevent any content from overflowing
    lineHeight: 1, // Prevent line height from affecting size
  },
  adBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: '#f8f9fa',
    border: 'none',
    borderTop: '1px solid #e0e0e0',
    padding: '0', // No padding to maximize ad space
    position: 'relative',
    height: '60px', // Height to match standard ad unit
    maxHeight: '60px', // Max height constraint
    overflow: 'hidden', // Prevent overflow
    boxSizing: 'border-box', // Include padding in height calculation
  },
  adBannerMobile: {
    padding: '0',
    height: '50px', // Height for standard mobile banner
    maxHeight: '50px',
  },
  adContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: '900px',
    position: 'relative',
  },
  adText: {
    color: '#5f6368',
    fontSize: '0.9rem',
    fontWeight: 400,
    marginRight: theme.spacing(2),
    flex: 1,
  },
  upgradeButton: {
    backgroundColor: '#4d79ff',
    color: 'white',
    '&:hover': {
      backgroundColor: '#3a68ff',
    },
    whiteSpace: 'nowrap',
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    color: '#5f6368',
    padding: 4,
  },
  adLabel: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(0.5),
    backgroundColor: '#eaeaea',
    padding: '2px 4px',
    borderRadius: '2px',
    fontSize: '0.6rem',
    color: '#666',
    fontWeight: 500,
    zIndex: 2,
  },
  adImage: {
    maxHeight: '40px',
    marginRight: theme.spacing(2),
  },
  // Add specific styles for landscape orientation on mobile
  adBannerLandscape: {
    minHeight: '40px',
    padding: theme.spacing(0.25),
  },
}));

const GoogleAdsBanner = ({ userAddress, isAuthenticated, userID }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  // Banner visibility is now only dependent on authentication status
  const [showBanner, setShowBanner] = useState(!isAuthenticated && !isDebugFakePremium());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const isLandscape = window.innerHeight < window.innerWidth && isMobile;
  const [adsLoaded, setAdsLoaded] = useState(false);
  
  // Load Google AdSense script
  useEffect(() => {
    if (!isAuthenticated && !isDebugFakePremium()) {
      // Don't load ads for authenticated/premium users
      const script = document.createElement('script');
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9302777601495141';
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => setAdsLoaded(true);
      
      document.head.appendChild(script);
      
      return () => {
        // Cleanup script on unmount
        try {
          document.head.removeChild(script);
        } catch (e) {
          // Script might have been removed already
        }
      };
    }
  }, [isAuthenticated]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if user has premium status
  useEffect(() => {
    // If user is authenticated or debug fake premium is active, consider them premium
    const isPremium = isAuthenticated || isDebugFakePremium();
    setShowBanner(!isPremium);
  }, [isAuthenticated]);
  
  // Initialize AdSense ads with standard sizes
  useEffect(() => {
    if (adsLoaded && window.adsbygoogle && !isAuthenticated && !isDebugFakePremium()) {
      try {
        const adElement = document.querySelector('.adsbygoogle');
        if (adElement) {
          // Set a MutationObserver to ensure ad is sized correctly
          const observer = new MutationObserver((mutations) => {
            // Get screen width to determine if we're in mobile mode
            const isMobileView = window.innerWidth <= 600;
            const adHeight = isMobileView ? '50px' : '60px';
            const adWidth = isMobileView ? '320px' : '468px';
            
            // Force proper container size
            const container = adElement.parentElement;
            if (container && container.style) {
              container.style.height = adHeight;
              container.style.maxHeight = adHeight;
              container.style.overflow = 'hidden';
            }
            
            // Force correct size on any iframe children
            const iframes = adElement.querySelectorAll('iframe');
            iframes.forEach(iframe => {
              iframe.style.maxHeight = adHeight;
              iframe.style.height = adHeight;
              iframe.style.width = adWidth;
              iframe.style.overflow = 'hidden';
            });
          });
          
          // Start observing with configuration
          observer.observe(adElement, { 
            childList: true, 
            subtree: true,
            attributes: true 
          });
          
          // Initialize the ad
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch (e) {
        console.error('AdSense error:', e);
      }
    }
  }, [adsLoaded, isAuthenticated, isMobile]);

  // Remove handleClose function to prevent banner dismissal
  // Banner will now always be visible for non-authenticated users
  
  const handleUpgradeClick = () => {
    console.log('GoogleAdsBanner: UPGRADE NOW button clicked');
    
    if (isAuthenticated) {
      console.log('GoogleAdsBanner: Using global handler for authenticated user');
      // Use our global handler that ensures auth state is preserved
      if (window.navigateToSignup) {
        window.navigateToSignup();
      } else {
        // Fallback if global handler isn't available
        console.log('GoogleAdsBanner: Global handler not found, using context-aware approach');
        // Make sure we preserve the existing username (email) from localStorage
        const currentUsername = localStorage.getItem('username');
        
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userID', userID || '');
        localStorage.setItem('username', currentUsername || userID || ''); // Preserve email as username
        window.location.href = '/signup';
      }
    } else {
      // For non-authenticated users, regular navigation is fine
      console.log('GoogleAdsBanner: Non-authenticated user, using React Router');
      navigate('/signup');
    }
  };

  // Don't show the banner if user has premium status or has closed it
  if (!showBanner) {
    return null;
  }

  return (
    <div id='adBannerContainer' className={classes.adBannerRoot}>
      <Paper 
        elevation={0} 
        className={`${classes.adBanner} ${isMobile ? classes.adBannerMobile : ''} ${isLandscape ? classes.adBannerLandscape : ''}`}
      >
        <span className={classes.adLabel}>Ad</span>
        <Box className={classes.adContent}>
          {/* Google AdSense Ad with standard sizes */}
          <div style={{ 
            width: '100%', 
            height: isMobile ? '50px' : '60px', 
            overflow: 'hidden', 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <ins className="adsbygoogle"
              style={{ 
                display: 'inline-block', 
                width: isMobile ? '320px' : '468px', 
                height: isMobile ? '50px' : '60px',
                overflow: 'hidden'
              }}
              data-ad-client="ca-pub-9302777601495141"
              data-ad-slot="9468223728" // Ad slot ID for E3D banner ad
              data-ad-format="horizontal"
              data-full-width-responsive="false">
            </ins>
          </div>
          {/* No close button to prevent dismissing the ad */}
        </Box>
      </Paper>
    </div>
  );
};

export default GoogleAdsBanner;
